import React from "react";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import PageHeader from "Components/PageHeader";
import { SbSelect, SbTabs, SbTooltip, TooltipTriggerStyled } from "uiCore";

import {
  PAIC_VIEW_MODES,
  VIEW_MODE_LABELS,
} from "constants/peoplePlusAI.constants";
import {
  HeaderSection,
  CloseButton,
  TabsBlock,
  TopTabsRow,
  DocumentTypeSelectorWrapper,
  NavButton,
  DocumentTypeAlignedRow,
  HeaderControlsGroup,
  NavigationContainer,
  InlineTabsAndNavContainer,
  TabsFlexContainer,
  NavigationFlexContainer,
  SelectControlWrapper,
} from "Pages/PeoplePlusAIV2/shared/DocumentView.styles";


/**
 * Shared document header component for all PAIC document views
 * Replaces: PACJdPrdHeaderV2, PACJdViewHeaderV2, PACPeopleJDHeaderV2
 */
const SbPACDocumentHeaderV2 = ({
  title,
  viewMode,
  activeTab,
  tabItems,
  onTabChange,

  showDocumentTypeSelector = false,
  documentType,
  documentTypeOptions,
  onDocumentTypeChange,
  documentTypeTooltip,

  showNavigation = false,
  selectedItemId,
  itemOptions,
  onItemChange,
  onPrev,
  onNext,
  navigationPlaceholder = "Select item",
  selectItemTooltip,
  prevTooltip,
  nextTooltip,

  navigationLayout = "separate",

  onClose,
  closeTooltip,
  getPopupContainer,
}) => {
  const renderDocumentTypeSelector = () => {
    if (!showDocumentTypeSelector) return null;

    const selector = (
      <SbSelect
        value={documentType}
        options={documentTypeOptions}
        onChange={onDocumentTypeChange}
        showSearch={false}
        getPopupContainer={getPopupContainer}
      />
    );

    return (
      <DocumentTypeAlignedRow>
        <DocumentTypeSelectorWrapper>
          {documentTypeTooltip ? (
            <SbTooltip content={documentTypeTooltip}>
              <TooltipTriggerStyled>{selector}</TooltipTriggerStyled>
            </SbTooltip>
          ) : (
            selector
          )}
        </DocumentTypeSelectorWrapper>
      </DocumentTypeAlignedRow>
    );
  };

const renderNavigation = () => {
  if (!showNavigation) return null;

  const prevButton = (
    <NavButton
      type="primary"
      icon={<LeftOutlined />}
      onClick={onPrev}
      disabled={!itemOptions?.length}
    />
  );

  const nextButton = (
    <NavButton
      type="primary"
      icon={<RightOutlined />}
      onClick={onNext}
      disabled={!itemOptions?.length}
    />
  );

  const selectControl = (
    <SbSelect
      value={selectedItemId || undefined}
      options={itemOptions}
      onChange={onItemChange}
      placeholder={navigationPlaceholder}
      showSearch
      optionFilterProp="label"
      getPopupContainer={getPopupContainer}
    />
  );

  return (
    <NavigationContainer>
      {prevTooltip ? (
        <SbTooltip content={prevTooltip}>
          <TooltipTriggerStyled>{prevButton}</TooltipTriggerStyled>
        </SbTooltip>
      ) : (
        prevButton
      )}

      <SelectControlWrapper>
        {selectItemTooltip ? (
          <SbTooltip content={selectItemTooltip}>
            <TooltipTriggerStyled>{selectControl}</TooltipTriggerStyled>
          </SbTooltip>
        ) : (
          selectControl
        )}
      </SelectControlWrapper>

      {nextTooltip ? (
        <SbTooltip content={nextTooltip}>
          <TooltipTriggerStyled>{nextButton}</TooltipTriggerStyled>
        </SbTooltip>
      ) : (
        nextButton
      )}
    </NavigationContainer>
  );
};

  const renderTabsAndControls = () => {
    if (navigationLayout === "inline" && showNavigation) {
      return (
        <InlineTabsAndNavContainer>
          <TabsFlexContainer>
            <TopTabsRow>
              <SbTabs
                activeKey={activeTab}
                items={tabItems}
                onChange={onTabChange}
              />
            </TopTabsRow>
          </TabsFlexContainer>

          <NavigationFlexContainer>
            {renderNavigation()}
          </NavigationFlexContainer>
        </InlineTabsAndNavContainer>
      );
    }

    return (
      <>
        <TopTabsRow>
          <SbTabs
            activeKey={activeTab}
            items={tabItems}
            onChange={onTabChange}
          />
        </TopTabsRow>

        <HeaderControlsGroup>
          {renderDocumentTypeSelector()}
          {renderNavigation()}
        </HeaderControlsGroup>
      </>
    );
  };

  const closeButton = (
    <CloseButton type="primary" icon={<CloseOutlined />} onClick={onClose} />
  );

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`View Mode: ${
          VIEW_MODE_LABELS[viewMode] || VIEW_MODE_LABELS[PAIC_VIEW_MODES.VIEW]
        }`}
        actions={
          closeTooltip ? (
            <SbTooltip content={closeTooltip}>
              <TooltipTriggerStyled>{closeButton}</TooltipTriggerStyled>
            </SbTooltip>
          ) : (
            closeButton
          )
        }
      />

      <HeaderSection>
        <TabsBlock>{renderTabsAndControls()}</TabsBlock>
      </HeaderSection>
    </>
  );
};

export default SbPACDocumentHeaderV2;